/*
 * Rebuy Smart Cart enhancements.
 *
 * Extracted from layout/theme.liquid so it is cached across pageviews and
 * parsed off the critical path. Liquid-derived values arrive via the
 * `window.smartCartConfig` object emitted just before this file loads.
 */

(function () {
  let config = window.smartCartConfig || {};
  let mutationDelayMs = 150;
  let fallbackDelayMs = 500;
  let mutationTimer;
  let checkoutLabelObserver;
  let observedCheckoutButton;
  let checkoutButtonMountObserver;
  let observedCheckoutMountRoot;
  let smartCartDocumentObserver;
  let checkoutLabelUpdateQueued = false;
  let isUpdatingCheckoutLabel = false;
  let enhancementTimers = [];
  let cartCountRefreshTimers = [];
  let recommendationRefreshTimers = [];
  let recommendationRefreshInFlight = false;
  let recommendationRefreshReleaseTimer;
  let minimumVisibleRecommendationProducts = 2;
  let smartCartItemCount = null;
  let smartCartItemCountRequestId = 0;
  let showAddedTitle = false;
  let cartJsonUrl = config.cartJsonUrl || '/cart.js';
  let emptyCartShopButtonStyle = [
    'display: flex',
    'align-items: center',
    'justify-content: center',
    'min-height: 38px',
    'width: 100%',
    'padding: 10px 15px',
    'border: 1px solid #000000',
    'background: #000000',
    'color: #ffffff',
    'font-family: "Cloister CG", Avenir-Next, sans-serif',
    'font-size: 16px',
    'font-weight: 400',
    'line-height: 1',
    'letter-spacing: 0',
    'text-align: center',
    'text-decoration: none',
    'text-transform: none',
    'text-indent: 0',
    'box-sizing: border-box',
    'overflow: visible',
    'cursor: pointer',
    'transition: background-color 0.25s ease, border-color 0.25s ease, color 0.25s ease',
    'opacity: 1',
    'visibility: visible'
  ].join('; ');
  let emptyCartShopButtonHoverStyle = emptyCartShopButtonStyle
    .replace('border: 1px solid #000000', 'border: 1px solid #292923')
    .replace('background: #000000', 'background: #292923');
  let emptyCartShopButtonLabelStyle = [
    'display: block',
    'color: #ffffff',
    '-webkit-text-fill-color: #ffffff',
    'font-family: "Cloister CG", Avenir-Next, sans-serif',
    'font-size: 16px',
    'font-weight: 400',
    'line-height: 1',
    'letter-spacing: 0',
    'text-align: center',
    'text-decoration: none',
    'text-transform: none',
    'text-indent: 0',
    'white-space: nowrap',
    'opacity: 1',
    'visibility: visible',
    'position: relative',
    'z-index: 1',
    'transform: none',
    'clip-path: none',
    'pointer-events: none'
  ].join('; ');
  let uvpIcons = config.uvpIcons || { usa: '', globe: '' };
  let addedTitleHtml =
    '<span class="smartcart-added-title" style="display:inline-flex;align-items:center;gap:8px;">' +
    '<svg width="11" height="11" viewBox="0 0 13 11" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M0.304932 5.79462L5.40709 9.71936L12.4716 0.299988" stroke="black" stroke-linejoin="round"/>' +
    '</svg>' +
    '<span>An item Was added to cart</span>' +
    '</span>';

  function getCorsoCheckoutButton() {
    return document.querySelector('.rebuy-cart__checkout-button[data-corso-checkout-plus="clone"]');
  }

  function getPrimaryCheckoutButton() {
    let corsoButton = getCorsoCheckoutButton();
    let buttons;
    let i;

    if (corsoButton) return corsoButton;

    buttons = document.querySelectorAll('.rebuy-cart__checkout-button');

    for (i = 0; i < buttons.length; i += 1) {
      if (!/without shipping protection/i.test(buttons[i].textContent || '')) {
        return buttons[i];
      }
    }

    return null;
  }

  function getSmartCartRoot() {
    return document.querySelector('.rebuy-cart__flyout, .rebuy-cart, .rebuy-modal') || document.body;
  }

  function normalizeMoneySpacing(value) {
    return String(value || '')
      .replace(/&nbsp;/g, ' ')
      .replace(/([\u0024\u00a3\u20ac\u00a5])[\s\u00a0]+(?=\d)/g, '$1');
  }

  function formatCartMoney(cents) {
    let formattedMoney;

    if (window.theme && window.theme.formatMoney) {
      formattedMoney = window.theme.formatMoney(
        cents,
        window.theme.moneyWithoutCurrencyFormat || window.theme.moneyFormat
      );

      return normalizeMoneySpacing(formattedMoney);
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  }

  function moneyTextToCents(value) {
    let normalized = String(value || '').replace(/[^0-9,.-]/g, '');
    let commaIndex = normalized.lastIndexOf(',');
    let dotIndex = normalized.lastIndexOf('.');
    let amount;

    if (!normalized) return null;

    if (commaIndex > dotIndex) {
      normalized = normalized.replace(/\./g, '').replace(',', '.');
    } else {
      normalized = normalized.replace(/,/g, '');
    }

    amount = parseFloat(normalized);

    return isNaN(amount) ? null : Math.round(amount * 100);
  }

  function findMoneyCents(text) {
    let match = String(text || '').match(/(?:[$\u00a3\u20ac\u00a5]\s*)\d[\d,.]*/);

    return match ? moneyTextToCents(match[0]) : null;
  }

  function getProtectedCheckoutTotalCents() {
    let root = getSmartCartRoot();
    let text = root ? root.textContent.replace(/\s+/g, ' ') : '';
    let subtotalIndex = text.toUpperCase().lastIndexOf('SUBTOTAL');
    let subtotalText;
    let subtotal;
    let protectionIndex;
    let protection;

    if (subtotalIndex < 0) return null;

    subtotalText = text.slice(subtotalIndex);
    subtotal = findMoneyCents(subtotalText);
    protectionIndex = subtotalText.toLowerCase().indexOf('shipping protection');

    if (subtotal === null) return null;

    if (protectionIndex < 0) return subtotal;

    protection = findMoneyCents(subtotalText.slice(protectionIndex));

    if (protection === null) return subtotal;

    return subtotal + protection;
  }

  function parsePositiveInteger(value) {
    let match = String(value || '').match(/\d+/);
    let number = match ? parseInt(match[0], 10) : NaN;

    return number > 0 ? number : null;
  }

  function setSmartCartItemCount(value) {
    let count = parseInt(value, 10);

    if (isNaN(count) || count < 0) return false;

    smartCartItemCount = count;
    return true;
  }

  function requestSmartCartItemCount() {
    let requestId = smartCartItemCountRequestId + 1;

    smartCartItemCountRequestId = requestId;

    return fetch(cartJsonUrl, {
      cache: 'no-store',
      credentials: 'same-origin'
    })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (cart) {
        if (
          requestId !== smartCartItemCountRequestId ||
          !cart ||
          typeof cart.item_count === 'undefined' ||
          !setSmartCartItemCount(cart.item_count)
        ) {
          return false;
        }

        applyProtectedCheckoutPlaceholder();
        observeCheckoutButtonLabel();

        return true;
      })
      .catch(function () {
        return false;
      });
  }

  function clearCartCountRefreshTimers() {
    while (cartCountRefreshTimers.length) {
      clearTimeout(cartCountRefreshTimers.pop());
    }
  }

  function clearRecommendationRefreshTimers() {
    while (recommendationRefreshTimers.length) {
      clearTimeout(recommendationRefreshTimers.pop());
    }
  }

  function releaseRecommendationRefreshLock() {
    clearTimeout(recommendationRefreshReleaseTimer);
    recommendationRefreshInFlight = false;
  }

  function acquireRecommendationRefreshLock() {
    if (recommendationRefreshInFlight) return false;

    recommendationRefreshInFlight = true;
    recommendationRefreshReleaseTimer = setTimeout(releaseRecommendationRefreshLock, 5000);

    return true;
  }

  function scheduleSmartCartItemCountRefresh() {
    let retryDelays = [0, 150, 500, 1000];

    clearCartCountRefreshTimers();

    retryDelays.forEach(function (delay) {
      cartCountRefreshTimers.push(setTimeout(requestSmartCartItemCount, delay));
    });
  }

  function getHeaderCartItemCount() {
    let counts = document.querySelectorAll('[data-cart-count]');
    let count;
    let i;

    for (i = 0; i < counts.length; i += 1) {
      count = parsePositiveInteger(counts[i].getAttribute('data-cart-count') || counts[i].textContent);

      if (count !== null) return count;
    }

    return null;
  }

  function getSmartCartItemCount() {
    return smartCartItemCount !== null ? smartCartItemCount : getHeaderCartItemCount();
  }

  function formatCheckoutItemsLabel(count) {
    return 'PROCEED TO CHECKOUT (' + count + ' ' + (count === 1 ? 'Item' : 'Items') + ')';
  }

  function applyProtectedCheckoutPlaceholder() {
    let button = getPrimaryCheckoutButton();
    let itemCount;
    let label;

    if (!button) return false;

    itemCount = getSmartCartItemCount();

    if (itemCount === null || itemCount < 1) return false;

    label = formatCheckoutItemsLabel(itemCount);

    button.setAttribute('data-catori-protected-placeholder', 'true');
    button.setAttribute('data-catori-checkout-label', label);
    button.setAttribute('aria-label', label);

    if ((button.textContent || '').trim() !== label) {
      isUpdatingCheckoutLabel = true;

      try {
        button.textContent = label;
      } finally {
        isUpdatingCheckoutLabel = false;
      }
    }

    return true;
  }

  function scheduleCheckoutLabelCorrection() {
    let runOnFrame;

    if (checkoutLabelUpdateQueued) return;

    checkoutLabelUpdateQueued = true;
    runOnFrame = window.requestAnimationFrame || function (callback) {
      return setTimeout(callback, 16);
    };

    runOnFrame(function () {
      checkoutLabelUpdateQueued = false;
      applyProtectedCheckoutPlaceholder();
      observeCheckoutButtonLabel();
    });
  }

  function observeCheckoutButtonLabel() {
    let button = getPrimaryCheckoutButton();

    if (!button) return false;

    if (!window.MutationObserver) return true;

    if (observedCheckoutButton === button) return true;

    if (checkoutLabelObserver) {
      checkoutLabelObserver.disconnect();
    }

    observedCheckoutButton = button;
    checkoutLabelObserver = new MutationObserver(function () {
      if (isUpdatingCheckoutLabel) return;

      scheduleCheckoutLabelCorrection();
    });

    checkoutLabelObserver.observe(button, {
      childList: true,
      characterData: true,
      subtree: true
    });

    return true;
  }

  function getEmptyCartShopButton() {
    let selectors = [
      '#rebuy-cart .rebuy-cart__flyout-empty-cart .catori-empty-cart-shop-now',
      '#rebuy-cart .rebuy-cart__flyout-empty-cart a[href*="/collections/shop-all"]',
      '#rebuy-cart .rebuy-cart__flyout-empty-cart p.ql-align-center > a[href*="/collections/"]',
      '#rebuy-cart .rebuy-cart__flyout-empty-cart a[href*="/collections/"]'
    ];
    let button;
    let i;

    for (i = 0; i < selectors.length; i += 1) {
      button = document.querySelector(selectors[i]);
      if (button) return button;
    }

    return null;
  }

  function getSmartCartRecommendationsRoot() {
    return document.querySelector('#rebuy-cart .rebuy-cart__flyout-recommendations');
  }

  function getSmartCartRecommendationNodes() {
    let root = getSmartCartRecommendationsRoot();

    return root ? root.querySelectorAll('[data-rebuy-id]') : [];
  }

  function getRebuyWidgetById(widgetId) {
    let widgets = window.Rebuy && window.Rebuy.widgets;
    let i;

    if (!widgets || !widgets.length) return null;

    for (i = 0; i < widgets.length; i += 1) {
      if (String(widgets[i].id) === String(widgetId)) return widgets[i];
    }

    return null;
  }

  function getLatestRebuyCart() {
    if (window.Rebuy && window.Rebuy.SmartCart && window.Rebuy.SmartCart.cart) {
      return window.Rebuy.SmartCart.cart;
    }

    if (window.Rebuy && window.Rebuy.Cart && window.Rebuy.Cart.cart) {
      return window.Rebuy.Cart.cart;
    }

    return null;
  }

  function getSmartCartRecommendationProductCount() {
    let root = getSmartCartRecommendationsRoot();
    let products;

    if (!root) return 0;

    products = root.querySelectorAll('.rebuy-product-block');

    return products.length;
  }

  function smartCartRecommendationsHaveEnoughProducts() {
    return getSmartCartRecommendationProductCount() >= minimumVisibleRecommendationProducts;
  }

  function refreshRebuyWidgetNode(node, onComplete) {
    let widgetId = node.getAttribute('data-rebuy-id');
    let widget = getRebuyWidgetById(widgetId);
    let latestCart = getLatestRebuyCart();

    onComplete = onComplete || function () {};

    if (widget && widget.data && latestCart) {
      widget.data.cart = latestCart;
    }

    if (widget && typeof widget.getWidgetProducts === 'function') {
      try {
        widget.getWidgetProducts(function () {
          if (widget.View && widget.View.vue && typeof widget.View.vue.$forceUpdate === 'function') {
            widget.View.vue.$forceUpdate();
          } else if (typeof widget.render === 'function') {
            widget.render();
          }

          scheduleSmartCartEnhancements();
          onComplete();
        });
      } catch (error) {
        onComplete();
      }

      return true;
    }

    node.innerHTML = '';
    node.removeAttribute('data-initialized');

    if (window.Rebuy && window.Rebuy.widgets && window.Rebuy.widgets.length) {
      window.Rebuy.widgets = window.Rebuy.widgets.filter(function (rebuyWidget) {
        return String(rebuyWidget.id) !== String(widgetId);
      });
    }

    onComplete();

    return false;
  }

  function refreshSmartCartRecommendations() {
    let nodes = getSmartCartRecommendationNodes();
    let didUseWidgetApi = false;
    let needsRebuyInit = false;
    let pendingRefreshes;
    let i;

    if (!nodes.length || smartCartRecommendationsHaveEnoughProducts()) return false;
    if (!acquireRecommendationRefreshLock()) return false;

    pendingRefreshes = nodes.length;

    function completeRefresh() {
      pendingRefreshes -= 1;

      if (pendingRefreshes <= 0) {
        releaseRecommendationRefreshLock();
      }
    }

    for (i = 0; i < nodes.length; i += 1) {
      if (refreshRebuyWidgetNode(nodes[i], completeRefresh)) {
        didUseWidgetApi = true;
      } else {
        needsRebuyInit = true;
      }
    }

    if (needsRebuyInit && window.Rebuy && typeof window.Rebuy.init === 'function') {
      window.Rebuy.init();
    }

    return didUseWidgetApi || needsRebuyInit;
  }

  function scheduleSmartCartRecommendationsRefresh() {
    let retryDelays = [250, 750, 1500, 3000];

    clearRecommendationRefreshTimers();

    retryDelays.forEach(function (delay) {
      recommendationRefreshTimers.push(setTimeout(refreshSmartCartRecommendations, delay));
    });
  }

  function textMatchesSecondaryCheckout(value) {
    return /checkout without shipping protection/i.test(value || '');
  }

  function getSecondaryCheckoutButton() {
    let root = document.querySelector('#rebuy-cart') || getSmartCartRoot();
    let buttons = root.querySelectorAll('a, button, [role="button"], .rebuy-cart__checkout-button');
    let i;

    for (i = 0; i < buttons.length; i += 1) {
      if (textMatchesSecondaryCheckout(buttons[i].textContent)) {
        return buttons[i];
      }
    }

    return null;
  }

  function applyEmptyCartShopButton() {
    let button = getEmptyCartShopButton();
    let label;

    if (!button) return false;

    button.classList.add('catori-empty-cart-shop-now');

    if (!button.getAttribute('href')) {
      button.setAttribute('href', '/collections/shop-all');
    }

    if (button.getAttribute('aria-label') !== 'Shop Now') {
      button.setAttribute('aria-label', 'Shop Now');
    }

    if (button.getAttribute('data-catori-empty-cart-shop-now') !== 'true') {
      button.setAttribute('data-catori-empty-cart-shop-now', 'true');
    }

    if (button.getAttribute('style') !== emptyCartShopButtonStyle) {
      button.setAttribute('style', emptyCartShopButtonStyle);
    }

    if (button.getAttribute('data-catori-empty-cart-hover-bound') !== 'true') {
      button.setAttribute('data-catori-empty-cart-hover-bound', 'true');

      button.addEventListener('mouseenter', function () {
        button.setAttribute('style', emptyCartShopButtonHoverStyle);
      });

      button.addEventListener('mouseleave', function () {
        button.setAttribute('style', emptyCartShopButtonStyle);
      });

      button.addEventListener('focus', function () {
        button.setAttribute('style', emptyCartShopButtonHoverStyle);
      });

      button.addEventListener('blur', function () {
        button.setAttribute('style', emptyCartShopButtonStyle);
      });
    }

    label = button.querySelector('[data-catori-empty-cart-shop-now-label]');

    if (!label || (button.textContent || '').trim() !== 'Shop Now') {
      while (button.firstChild) {
        button.removeChild(button.firstChild);
      }

      label = document.createElement('span');
      label.setAttribute('data-catori-empty-cart-shop-now-label', 'true');
      label.textContent = 'Shop Now';
      button.appendChild(label);
    }

    if (label.getAttribute('style') !== emptyCartShopButtonLabelStyle) {
      label.setAttribute('style', emptyCartShopButtonLabelStyle);
    }

    return true;
  }

  function nodeHasCheckoutButton(node) {
    if (!node || node.nodeType !== 1) return false;

    return (
      node.matches('.rebuy-cart__checkout-button') ||
      textMatchesSecondaryCheckout(node.textContent) ||
      Boolean(node.querySelector('.rebuy-cart__checkout-button'))
    );
  }

  function nodeHasEmptyCartShopButton(node) {
    if (!node || node.nodeType !== 1) return false;

    return (
      node.matches('.rebuy-cart__flyout-empty-cart .catori-empty-cart-shop-now') ||
      node.matches('.rebuy-cart__flyout-empty-cart a[href*="/collections/"]') ||
      Boolean(node.querySelector('.rebuy-cart__flyout-empty-cart .catori-empty-cart-shop-now, .rebuy-cart__flyout-empty-cart a[href*="/collections/"]'))
    );
  }

  function mutationTargetsEmptyCartShopButton(mutation) {
    let target = mutation.target && mutation.target.nodeType === 1 ? mutation.target : mutation.target.parentElement;

    return Boolean(
      target &&
      (
        nodeHasEmptyCartShopButton(target) ||
        (
          target.closest &&
          target.closest('.rebuy-cart__flyout-empty-cart .catori-empty-cart-shop-now, .rebuy-cart__flyout-empty-cart a[href*="/collections/"]')
        )
      )
    );
  }

  function observeCheckoutButtonMounts() {
    let root = document.querySelector('.rebuy-cart__flyout, .rebuy-cart, .rebuy-modal');

    if (!root || !window.MutationObserver) return false;

    if (observedCheckoutMountRoot === root) return true;

    if (checkoutButtonMountObserver) {
      checkoutButtonMountObserver.disconnect();
    }

    observedCheckoutMountRoot = root;
    checkoutButtonMountObserver = new MutationObserver(function (mutations) {
      let i;
      let j;

      for (i = 0; i < mutations.length; i += 1) {
        if (
          mutations[i].target &&
          mutations[i].target.nodeType === 1 &&
          mutations[i].target.closest &&
          mutations[i].target.closest('.rebuy-cart__flyout-recommendations')
        ) {
          continue;
        }

        if (mutations[i].type === 'childList') {
          scheduleSmartCartEnhancements();
          return;
        }

        if (mutationTargetsEmptyCartShopButton(mutations[i])) {
          scheduleSmartCartEnhancements();
          return;
        }

        for (j = 0; j < mutations[i].addedNodes.length; j += 1) {
          if (
            nodeHasCheckoutButton(mutations[i].addedNodes[j]) ||
            nodeHasEmptyCartShopButton(mutations[i].addedNodes[j])
          ) {
            runSmartCartEnhancements();
            return;
          }
        }
      }
    });

    checkoutButtonMountObserver.observe(root, {
      childList: true,
      subtree: true
    });

    return true;
  }

  function observeSmartCartDocumentMounts() {
    if (!window.MutationObserver || smartCartDocumentObserver) return;

    smartCartDocumentObserver = new MutationObserver(function (mutations) {
      let i;
      let j;

      for (i = 0; i < mutations.length; i += 1) {
        if (
          mutations[i].target &&
          mutations[i].target.nodeType === 1 &&
          mutations[i].target.closest &&
          mutations[i].target.closest('#rebuy-cart')
        ) {
          if (mutations[i].target.closest('.rebuy-cart__flyout-recommendations')) {
            continue;
          }

          scheduleSmartCartEnhancements();
          return;
        }

        for (j = 0; j < mutations[i].addedNodes.length; j += 1) {
          if (
            nodeHasCheckoutButton(mutations[i].addedNodes[j]) ||
            nodeHasEmptyCartShopButton(mutations[i].addedNodes[j])
          ) {
            scheduleSmartCartEnhancements();
            return;
          }
        }
      }
    });

    smartCartDocumentObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  function applySmartCartAddedTitle() {
    let title = document.getElementById('SmartCart_title');
    if (!title) return false;

    title.innerHTML = addedTitleHtml;
    return true;
  }

  function scheduleSmartCartAddedTitle() {
    if (applySmartCartAddedTitle()) return;

    setTimeout(function () {
      applySmartCartAddedTitle();
    }, fallbackDelayMs);
  }

  function scheduleAfterCartMutation() {
    clearTimeout(mutationTimer);
    scheduleSmartCartItemCountRefresh();
    scheduleSmartCartRecommendationsRefresh();
    mutationTimer = setTimeout(scheduleSmartCartEnhancements, mutationDelayMs);
  }

  function smartCartControlShouldRefresh(control) {
    let controlData = [
      control.textContent,
      control.getAttribute('aria-label'),
      control.getAttribute('class'),
      control.getAttribute('name'),
      control.getAttribute('data-action')
    ].join(' ');

    return /increase|decrease|increment|decrement|quantity|remove|plus|minus|\+|(^|\s)-(\s|$)/i.test(controlData);
  }

  function handleSmartCartControlInteraction(event) {
    let control = event.target.closest && event.target.closest('#rebuy-cart button, #rebuy-cart a, #rebuy-cart input, #rebuy-cart [role="button"]');

    if (!control || !smartCartControlShouldRefresh(control)) return;
    if (control.closest && control.closest('.rebuy-cart__flyout-recommendations')) return;

    scheduleAfterCartMutation();
  }

  function buildCartUvpsHtml() {
    return (
      '<div class="cart-uvps" data-cart-uvps>' +
        '<div class="cart-uvps__item">' +
          '<span class="cart-uvps__icon">' + uvpIcons.usa + '</span>' +
          '<span class="cart-uvps__text">Ethically Made in USA</span>' +
        '</div>' +
        '<div class="cart-uvps__item">' +
          '<span class="cart-uvps__icon">' + uvpIcons.globe + '</span>' +
          '<span class="cart-uvps__text">Global, Fast &amp; Secure</span>' +
        '</div>' +
      '</div>'
    );
  }

  function injectCartUvps() {
    let wrapper = getSecondaryCheckoutButton() || getPrimaryCheckoutButton();
    let existing = document.querySelector('[data-cart-uvps]');

    if (!wrapper) return false;

    if (existing) {
      if (existing.previousElementSibling !== wrapper) {
        wrapper.insertAdjacentElement('afterend', existing);
      }
      return true;
    }

    let container = document.createElement('div');
    container.innerHTML = buildCartUvpsHtml();
    wrapper.insertAdjacentElement('afterend', container.firstElementChild);
    return true;
  }

  function runSmartCartEnhancements() {
    let protectedLabelReady = applyProtectedCheckoutPlaceholder();
    let uvpsReady = injectCartUvps();
    let labelObserverReady = observeCheckoutButtonLabel();
    let emptyCartShopButtonReady = applyEmptyCartShopButton();

    observeCheckoutButtonMounts();

    return (protectedLabelReady && uvpsReady && labelObserverReady) || emptyCartShopButtonReady;
  }

  function clearEnhancementTimers() {
    while (enhancementTimers.length) {
      clearTimeout(enhancementTimers.pop());
    }
  }

  function scheduleSmartCartEnhancements() {
    let runOnFrame = window.requestAnimationFrame || function (callback) {
      return setTimeout(callback, 16);
    };
    let retryDelays = [50, 150, 300, fallbackDelayMs, 1000, 2000, 4000];

    clearEnhancementTimers();

    runOnFrame(function () {
      runSmartCartEnhancements();

      retryDelays.forEach(function (delay) {
        enhancementTimers.push(setTimeout(function () {
          runSmartCartEnhancements();
        }, delay));
      });
    });
  }

  document.addEventListener('rebuy:smartcart.show', function () {
    scheduleSmartCartItemCountRefresh();
    scheduleSmartCartRecommendationsRefresh();
    scheduleSmartCartEnhancements();

    if (!showAddedTitle) return;

    if (applySmartCartAddedTitle()) {
      showAddedTitle = false;
      return;
    }

    setTimeout(function () {
      applySmartCartAddedTitle();
      showAddedTitle = false;
    }, fallbackDelayMs);
  });

  document.addEventListener('rebuy:loaded', function () {
    scheduleSmartCartItemCountRefresh();
    scheduleSmartCartRecommendationsRefresh();
    scheduleSmartCartEnhancements();
  });

  document.addEventListener('rebuy:cart.add', function () {
    showAddedTitle = true;
    scheduleSmartCartItemCountRefresh();
    scheduleSmartCartRecommendationsRefresh();
    scheduleSmartCartAddedTitle();
  });

  [
    'rebuy:cart.change',
    'rebuy:smartcart.line-item-increase',
    'rebuy:smartcart.line-item-decrease',
    'rebuy:smartcart.line-item-removed',
    'rebuy:smartcart.item-switch-to-subscription',
    'rebuy:smartcart.item-switch-to-one-time'
  ].forEach(function (eventName) {
    document.addEventListener(eventName, scheduleAfterCartMutation);
  });

  document.addEventListener('click', handleSmartCartControlInteraction, true);
  document.addEventListener('change', handleSmartCartControlInteraction, true);
  document.addEventListener('input', handleSmartCartControlInteraction, true);

  scheduleSmartCartItemCountRefresh();
  scheduleSmartCartRecommendationsRefresh();
  scheduleSmartCartEnhancements();
  observeSmartCartDocumentMounts();
})();
